import { MinusIcon } from 'lucide-react'
import * as React from 'react'

import { RiskLevel } from '@/gen/inventory/v1/risk_pb'

import { cn } from '@/lib/ui/style-helpers'

import { Tooltip } from '@/components/lemmala-design-system/tooltip/tooltip'
import { AlertSeverityBadge } from '@/components/shared/alert-severity-badge/alert-severity-badge'

import { FALLBACK_SEVERITY_ORDER, SEVERITY_ORDER } from './consts'
import { SeverityCount } from './severity-count'
import { SeverityCountStat } from './types'

export interface SeverityDistributionProps extends React.HTMLAttributes<HTMLDivElement> {
  countByLevel?: SeverityCountStat[]
  showMinusIcon?: boolean
}

export const SeverityDistribution = React.forwardRef<HTMLDivElement, SeverityDistributionProps>(
  ({ className, countByLevel = [], showMinusIcon = false, ...props }, ref) => {
    const validCounts = countByLevel.filter(
      (stat) => stat.count > 0 && stat.riskLevel !== RiskLevel.UNSPECIFIED,
    )

    const sortedCounts = validCounts.sort((a, b) => {
      const orderA = SEVERITY_ORDER[a.riskLevel] ?? FALLBACK_SEVERITY_ORDER
      const orderB = SEVERITY_ORDER[b.riskLevel] ?? FALLBACK_SEVERITY_ORDER
      return orderA - orderB
    })

    if (sortedCounts.length === 0 && !showMinusIcon) {
      return null
    }

    if (sortedCounts.length === 0 && showMinusIcon) {
      return (
        <div
          ref={ref}
          data-testid='severity-distribution'
          className={cn('inline-flex items-center gap-2', className)}
          {...props}
        >
          <MinusIcon className='text-main-foreground-placeholder' />
        </div>
      )
    }

    const tooltipContent = (
      <div>
        {sortedCounts.length > 0 && (
          <div className='space-y-2'>
            {sortedCounts.map((stat, index) => (
              <div key={`${stat.riskLevel}-${index}`} className='flex items-center gap-1'>
                <span className='w-6 text-body-default'>{stat.count}</span>
                <AlertSeverityBadge level={stat.riskLevel} />
              </div>
            ))}
          </div>
        )}
      </div>
    )

    const content = (
      <div
        ref={ref}
        data-testid='severity-distribution'
        className={cn('inline-flex items-center gap-2', className)}
        {...props}
      >
        {sortedCounts.map((stat, index) => (
          <SeverityCount key={`${stat.riskLevel}-${index}`} countStat={stat} />
        ))}
      </div>
    )

    return (
      <Tooltip trigger={content} tooltipDisabled={sortedCounts.length === 0}>
        {tooltipContent}
      </Tooltip>
    )
  },
)
SeverityDistribution.displayName = 'SeverityDistribution'
